import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useI18n, languages } from "@/i18n";
import { localizedHead } from "@/i18n/headTranslations";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SwipeableGallery, GalleryImage } from "@/components/bath/SwipeableGallery";
import { BathLeadForm } from "@/components/bath/BathLeadForm";
import { RelatedBathProducts } from "@/components/bath/RelatedBathProducts";
import { PrevNextBathNav } from "@/components/bath/PrevNextBathNav";
import { LocalizedLink } from "@/components/LocalizedLink";
import {
  CheckCircle2,
  ShieldCheck,
  Truck,
  Layers,
  Building2,
  Hotel,
  Home,
  Wrench,
  Sparkles,
  TrendingUp,
} from "lucide-react";

export interface BathProductConfig {
  slug: string;
  category: string;
  productName: string;
  shortName: string;
  sku: string;
  schemaCategory: string;
  metaTitle: string;
  metaDescription: string;
  heroEyebrow: string;
  heroHeadline: React.ReactNode;
  heroIntro: string;
  premiumSummary: string;
  galleryImages: GalleryImage[];
  specs: Array<{ label: string; value: string }>;
  whyChoose?: Array<{ icon: React.ComponentType<{ className?: string }>; title: string; body: string }>;
  perfectFor?: Array<{ icon: React.ComponentType<{ className?: string }>; title: string; body: string }>;
  longDescription: string[];
  quickFeatures?: string[];
  specsPdfButton?: React.ReactNode;
  /** Override default material for Product schema. */
  productMaterial?: string;
  /** Structured specs surfaced as schema.org PropertyValue entries. */
  additionalProperties?: Array<{ name: string; value: string; unitText?: string }>;
  /** FAQ entries — renders a visible FAQ section + FAQPage JSON-LD. */
  faqs?: Array<{ question: string; answer: string }>;
  /** Optional rich feature showcase rendered before the spec table. */
  featureShowcase?: React.ReactNode;
  /** Optional polished specs panel that replaces the default spec table. */
  specsPanel?: React.ReactNode;
}

const SITE_ORIGIN = "https://www.aureum7trading.com";

const DEFAULT_WHY_CHOOSE = [
  { icon: TrendingUp, title: "Higher perceived value per unit", body: "A sculpted solid surface piece elevates listing photography, show-flat impact, and resale appeal — for a fraction of European-brand cost." },
  { icon: ShieldCheck, title: "Solid surface durability", body: "Non-porous, repairable, warm to the touch. Outperforms ceramic and acrylic on stain resistance and longevity in residential and hospitality use." },
  { icon: Wrench, title: "Specifier-friendly installation", body: "Standard EU plumbing rough-in, predictable cut-outs, no exotic tools or structural reinforcement required." },
  { icon: Layers, title: "Reliable bulk supply", body: "MOQ-ready from 20 units. Predictable lead times for phased developments and multi-floor rollouts." },
  { icon: Sparkles, title: "Designer-grade silhouette", body: "Sculpted from a single seamless cast — reads as bespoke joinery, not catalogue plumbing." },
  { icon: CheckCircle2, title: "Margin-protective pricing", body: "Trade pricing structured to protect your project margin — luxury aesthetic without the European brand premium." },
];

const DEFAULT_PERFECT_FOR = [
  { icon: Home, title: "Apartments", body: "Branded residences and high-end rental developments seeking signature bathrooms." },
  { icon: Building2, title: "Villas", body: "Private villas and signature primary suites where this piece is the focal point." },
  { icon: Hotel, title: "Hotels", body: "Boutique hotels and serviced apartments needing repeatable, photographable luxury." },
  { icon: Wrench, title: "Renovations", body: "Bathroom renovations and refurbishment projects targeting a premium reposition." },
];

export const BathProductPage = ({ config }: { config: BathProductConfig }) => {
  const {
    slug, category, productName, shortName, sku, schemaCategory,
    metaTitle, metaDescription, heroEyebrow, heroHeadline, heroIntro,
    premiumSummary, galleryImages, specs, longDescription,
  } = config;

  const { language } = useI18n();
  const canonical = `${SITE_ORIGIN}/${language}/bath-sanitary/${slug}`;
  const head = localizedHead(language, `/bath-sanitary/${slug}`, { title: metaTitle, description: metaDescription });
  const whyChoose = config.whyChoose ?? DEFAULT_WHY_CHOOSE;
  const perfectFor = config.perfectFor ?? DEFAULT_PERFECT_FOR;
  const quickFeatures = config.quickFeatures;
  const specsPdfButton = config.specsPdfButton;
  const faqs = config.faqs;
  const productMaterial = config.productMaterial ?? "Solid Surface (Stone Resin Composite)";
  const additionalProperties = config.additionalProperties;

  // Smoothly open + flash anchor target (spec rows / FAQ details)
  useEffect(() => {
    const handleHash = () => {
      const id = window.location.hash.replace(/^#/, "");
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;
      if (el.tagName.toLowerCase() === "details") {
        (el as HTMLDetailsElement).open = true;
      }
      // Re-trigger CSS animation on repeat clicks of the same hash
      el.classList.remove("anchor-flash");
      // Force reflow
      void (el as HTMLElement).offsetWidth;
      el.classList.add("anchor-flash");
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <link rel="canonical" href={canonical} />
        {languages.map((l) => (
          <link
            key={l.code}
            rel="alternate"
            hrefLang={l.locale}
            href={`${SITE_ORIGIN}/${l.code}/bath-sanitary/${slug}`}
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}/en/bath-sanitary/${slug}`} />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE_ORIGIN}${galleryImages[0].src}`} />
        <meta property="og:image:alt" content={galleryImages[0].alt} />
        {galleryImages.slice(1).map((g) => (
          <meta key={g.src} property="og:image" content={`${SITE_ORIGIN}${g.src}`} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={head.title} />
        <meta name="twitter:description" content={head.description} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}${galleryImages[0].src}`} />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: productName,
          description: metaDescription,
          sku,
          mpn: sku,
          brand: { "@type": "Brand", name: "Aureum VII Trading" },
          manufacturer: { "@type": "Organization", name: "Aureum VII Trading" },
          category: schemaCategory,
          material: productMaterial,
          additionalProperty: [
            ...specs.map((s) => ({
              "@type": "PropertyValue",
              name: s.label,
              value: s.value,
            })),
            ...(additionalProperties ?? []).map((p) => ({
              "@type": "PropertyValue",
              name: p.name,
              value: p.value,
              ...(p.unitText ? { unitText: p.unitText } : {}),
            })),
          ],
          image: galleryImages.map((g) => ({
            "@type": "ImageObject",
            contentUrl: `${SITE_ORIGIN}${g.src}`,
            url: `${SITE_ORIGIN}${g.src}`,
            name: g.title,
            caption: g.caption,
            description: g.description,
          })),
          offers: {
            "@type": "Offer",
            url: canonical,
            priceCurrency: "EUR",
            price: 600,
            priceValidUntil: "2026-12-31",
            availability: "https://schema.org/InStock",
            businessFunction: "https://schema.org/Sell",
            itemCondition: "https://schema.org/NewCondition",
            eligibleQuantity: { "@type": "QuantitativeValue", minValue: 20, unitCode: "C62" },
            seller: { "@type": "Organization", name: "Aureum VII Trading" },
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: ["ES", "FR", "DE", "IT", "NL", "BE", "PT", "IE", "AT"],
              returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
              merchantReturnDays: 14,
              returnMethod: "https://schema.org/ReturnByMail",
              returnFees: "https://schema.org/FreeReturn",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: ["ES", "FR", "DE", "IT", "NL", "BE", "PT", "IE", "AT", "SE", "DK", "FI"],
              },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 10, maxValue: 40, unitCode: "DAY" },
              },
            },
          },
        })}</script>

        {/* FAQPage JSON-LD intentionally not emitted here — owner SEO rule: FAQPage schema only on /faq. */}

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: `${productName} Gallery`,
          url: `${canonical}#gallery`,
          associatedMedia: galleryImages.map((g) => ({
            "@type": "ImageObject",
            contentUrl: `${SITE_ORIGIN}${g.src}`,
            url: `${SITE_ORIGIN}${g.src}`,
            name: g.title,
            caption: g.caption,
            description: g.description,
            license: canonical,
            acquireLicensePage: `${SITE_ORIGIN}/en/contact`,
            creditText: "Aureum VII Trading",
            copyrightNotice: "© Aureum VII Trading",
          })),
        })}</script>
      </Helmet>

      <Navigation />

      {/* BREADCRUMB */}
      <div className="border-b border-neutral-200 bg-white pt-28">
        <div className="container mx-auto px-6 py-4 text-xs text-neutral-500 lg:px-12">
          <LocalizedLink to="/bath-sanitary" className="hover:text-amber-600">
            Bath &amp; Sanitary
          </LocalizedLink>
          <span className="mx-2 text-amber-500">/</span>
          <span className="text-neutral-700">{productName}</span>
        </div>
      </div>

      {/* HERO + GALLERY */}
      <section id="gallery" className="border-b border-neutral-200 bg-white py-16">
        <div className="container mx-auto grid gap-12 px-6 lg:grid-cols-2 lg:items-start lg:px-12">
          <div>
            <SwipeableGallery images={galleryImages} />
          </div>
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-amber-600">
              {sku}
            </p>
            <p className="mb-3 text-xs uppercase tracking-[0.2em] text-neutral-500">
              {heroEyebrow}
            </p>
            <h1 className="font-serif text-3xl font-bold leading-tight text-neutral-900 sm:text-4xl">
              {heroHeadline}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-600">{heroIntro}</p>

            <div className="mt-6 border-l-2 border-amber-500 bg-neutral-50 p-5">
              <p className="text-neutral-700">{premiumSummary}</p>
            </div>

            {quickFeatures && quickFeatures.length > 0 && (
              <ul className="mt-6 grid gap-2 sm:grid-cols-2">
                {quickFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-neutral-900 px-8 text-white hover:bg-neutral-800">
                <a href="#quote">CONTACT SALES</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-neutral-900 bg-transparent text-neutral-900 hover:bg-neutral-100">
                <a href="#quote">Add to Project List</a>
              </Button>
              {specsPdfButton}
            </div>
            <p className="mt-4 text-xs text-neutral-500">CE Certified · Solid Surface · MOQ-ready · EU shipping</p>
          </div>
        </div>
      </section>

      {/* DESCRIPTION */}
      <section className="border-b border-neutral-200 bg-neutral-50 py-20">
        <div className="container mx-auto max-w-4xl px-6 text-center lg:px-12">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">
            About {shortName}
          </p>
          <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
            PRODUCT SPECIFICATIONS
          </h2>
          <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
          <div className="mt-8 space-y-5 text-left text-neutral-700 leading-relaxed">
            {longDescription.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* OPTIONAL FEATURE SHOWCASE (e.g. Hydra Deluxe) */}
      {config.featureShowcase}

      {/* SPECS — custom panel overrides default table when provided */}
      {config.specsPanel ? (
        config.specsPanel
      ) : (
        <section className="border-b border-neutral-200 bg-white py-20">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="text-center">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Specifications</p>
              <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">{shortName} — Technical Specs</h2>
              <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
            </div>
            <dl className="mx-auto mt-12 grid max-w-5xl gap-x-10 gap-y-0 sm:grid-cols-2">
              {specs.map((s) => (
                <div
                  key={s.label}
                  id={`spec-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="anchor-flash flex justify-between border-b border-neutral-200 px-2 py-4 scroll-mt-28"
                >
                  <dt className="text-sm font-medium text-neutral-500">{s.label}</dt>
                  <dd className="text-sm text-neutral-900">{s.value}</dd>
                </div>
              ))}
            </dl>
            {specsPdfButton && (
              <div className="mx-auto mt-10 flex max-w-5xl justify-center">
                {specsPdfButton}
              </div>
            )}
          </div>
        </section>
      )}

      {/* WHY CHOOSE */}
      <section className="border-b border-neutral-200 bg-neutral-50 py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Why specify {shortName}</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
              Built for projects, priced for <span className="text-amber-600">developers</span>
            </h2>
            <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {whyChoose.map((f) => (
              <div key={f.title} className="border border-neutral-200 bg-white p-6 transition-colors hover:border-amber-500">
                <f.icon className="h-6 w-6 text-amber-600" />
                <h3 className="mt-4 font-serif text-lg font-semibold text-neutral-900">{f.title}</h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERFECT FOR */}
      <section className="border-b border-neutral-200 bg-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Perfect for</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">Where {shortName} performs best</h2>
            <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {perfectFor.map((f) => (
              <div key={f.title} className="border border-neutral-200 bg-neutral-50 p-6">
                <f.icon className="h-6 w-6 text-amber-600" />
                <h3 className="mt-3 font-serif text-lg font-semibold text-neutral-900">{f.title}</h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-b border-neutral-200 bg-neutral-50 py-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid items-center gap-6 lg:grid-cols-3">
            <div className="flex items-center gap-4 border border-neutral-200 bg-white p-5">
              <ShieldCheck className="h-10 w-10 text-amber-600" />
              <div>
                <p className="font-serif text-lg font-semibold text-neutral-900">CE Certified</p>
                <p className="text-xs text-neutral-500">European compliance for solid surface sanitary ware</p>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-neutral-200 bg-white p-5">
              <Building2 className="h-10 w-10 text-amber-600" />
              <div>
                <p className="font-serif text-lg font-semibold text-neutral-900">Specified in EU developments</p>
                <p className="text-xs text-neutral-500">Trusted by developers across the EU and the Gulf</p>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-neutral-200 bg-white p-5">
              <Truck className="h-10 w-10 text-amber-600" />
              <div>
                <p className="font-serif text-lg font-semibold text-neutral-900">European Logistics</p>
                <p className="text-xs text-neutral-500">Sea freight 30–40 days · Air freight 10–15 days</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <section id="faq" className="border-b border-neutral-200 bg-white py-20">
          <div className="container mx-auto max-w-4xl px-6 lg:px-12">
            <div className="text-center">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Frequently Asked</p>
              <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">{shortName} — FAQ</h2>
              <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
            </div>
            <dl className="mt-10 divide-y divide-neutral-200 border-y border-neutral-200">
              {faqs.map((f) => (
                <details
                  key={f.question}
                  id={`faq-${f.question.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60)}`}
                  className="anchor-flash group px-3 py-5 scroll-mt-28 open:bg-amber-50/60"
                >
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-6">
                    <dt className="font-serif text-lg font-semibold text-neutral-900">{f.question}</dt>
                    <span className="mt-1 flex-shrink-0 font-mono text-xs text-amber-600 transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <dd className="mt-3 text-sm leading-relaxed text-neutral-600">{f.answer}</dd>
                </details>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* LEAD CAPTURE */}
      <section id="quote" className="bg-white py-24">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Project Enquiry</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">Get a Quote for {shortName}</h2>
            <div className="mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 max-w-md text-neutral-600">
              Tell us about your development. A project specialist will respond within one business
              day with bulk pricing, lead times, and OEM options.
            </p>
            <div className="mt-8 space-y-3 text-sm text-neutral-600">
              <p>📧 <a className="text-amber-600 hover:underline" href="mailto:info@aureum7trading.com">info@aureum7trading.com</a></p>
              <p>📱 <a className="text-amber-600 hover:underline" href="https://wa.me/34642278939">+34 642 278 939 (WhatsApp)</a></p>
            </div>
          </div>
          <BathLeadForm productName={productName} productSku={sku} />
        </div>
      </section>

      {/* PREV / NEXT product navigation */}
      <PrevNextBathNav currentSlug={slug} />

      {/* RELATED PRODUCTS */}
      <RelatedBathProducts currentSlug={slug} />

      <Footer />
    </div>
  );
};

export default BathProductPage;
